import styles from './styles.module.less';
import PropTypes from 'prop-types';
import { useForm, FormProvider } from 'react-hook-form';
import { ControlledTextInput } from '~/shared/components';
import { Text } from '@bevy/bevy-design-system';
import { ArrowRightIcon } from '@bevy/bevy-design-system/deprecated';
import useInitiateCheckout from './api/useInitiateCheckout';

import { ContainedButton } from '@/shared/components/Modals';

import { translate } from '~/utils/language';

import '~/styles/index.less';

const EmbeddedInitiateCheckout = ({
  promoCode = '',
  billingCadence = 'monthly',
  trialCode = '',
} = {}) => {
  // is it stupid to use this for a single field form...?  maybe. I guess eventually we might want to
  // validate whether the email can do a free trial though this may expose some data.

  const mutation = useInitiateCheckout({
    promoCode,
    billingCadence,
    trialCode,
  });

  const formMethods = useForm({
    defaultValues: {
      email: '',
    },
  });

  const startFreeTrial = translate('selfserve.initiateCheckout.startFreeTrial', 'Start free trial');
  const getStarted = translate('selfserve.initiateCheckout.getStarted', "Get started");
  const emailLabel = translate('selfserve.initiateCheckout.emailLabel', 'Email');

  const mainButtonLabel = trialCode ? startFreeTrial : getStarted;

  const {
    handleSubmit,
    setError,
    formState: { errors },
  } = formMethods;

  const onSubmit = handleSubmit((data) =>
    mutation.mutate(data, {
      onError: (error) => {
        const statusCode = error?.response?.status;
        if (statusCode === 400) {
          // this should never happen, maybe if our regex isn't good enough.
          setError('email', {
            message: translate('selfserve.initiateCheckout.invalidEmail', 'Invalid email'),
          });
        } else if (statusCode === 429) {
          setError('root.serverError', {
            message: translate(
              'selfserve.initiateCheckout.tooManyRequests',
              'Too many requests. Consider turning off your VPN or public wi-fi if you are using one.',
            ),
          });
        } else {
          setError('root.serverError', {
            message: translate(
              'selfserve.initiateCheckout.serverError',
              'A server error occurred.',
            ),
          });
        }
      },
    }),
  );

  return (
    <FormProvider {...formMethods}>
      <div className={styles.content}>
        <ControlledTextInput
          name='email'
          size='medium'
          label={emailLabel}
          rules={{
            required: {
              value: true,
              message: translate('selfserve.initiateCheckout.emailRequired', 'Email is required'),
            },
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: translate('selfserve.initiateCheckout.invalidEmail', 'Invalid email'),
            },
          }}
        />
        <ContainedButton
          size='small'
          dataTestId='Start free trial'
          ariaLabel={mainButtonLabel}
          onClick={onSubmit}
          disabled={!!mutation.isLoading}
          spacing='standard'
        >
          {mainButtonLabel} <ArrowRightIcon size='small' />
        </ContainedButton>
        {errors.root?.serverError && (
          <Text variant='notification' color='error'>
            {errors.root.serverError.message}
          </Text>
        )}
      </div>
    </FormProvider>
  );
};

EmbeddedInitiateCheckout.propTypes = {
  promoCode: PropTypes.string,
  billingCadence: PropTypes.string,
  trialCode: PropTypes.string,
};

export default EmbeddedInitiateCheckout;
